import { useState, useEffect } from 'react';
import { Crown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

type SubscriptionPlan = 'free' | 'starter' | 'pro' | 'enterprise';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  suggestedPlan?: SubscriptionPlan;
  title?: string;
  message?: string;
  resetDate?: string;
}

export const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  suggestedPlan = 'starter',
  title = 'Upgrade Required',
  message = 'You have reached your usage limit. Upgrade to continue using OREOG.',
  resetDate 
}: UpgradeModalProps) => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>(suggestedPlan);

  useEffect(() => {
    // Listen for upgrade modal events
    const handleUpgradeEvent = (event: CustomEvent) => {
      // This will be triggered by the payment middleware
    };

    window.addEventListener('show-upgrade-modal', handleUpgradeEvent as EventListener);
    return () => {
      window.removeEventListener('show-upgrade-modal', handleUpgradeEvent as EventListener);
    };
  }, []);

  const handleUpgrade = () => {
    onClose();
    navigate('/upgrade');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Crown className="h-5 w-5 text-yellow-500" />
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">Upgrade your plan to continue using the application.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Limitation Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <p className="text-red-800 font-medium">{message}</p>
                {resetDate && (
                  <p className="text-red-600 text-sm mt-1">
                    Your free quota resets on {new Date(resetDate).toLocaleDateString('en-IN')}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm text-gray-600">Suggested plan</div>
            <div className="flex gap-2">
              {(['starter', 'pro'] as SubscriptionPlan[]).map((p) => (
                <Button
                  key={p}
                  type="button"
                  variant={p === selectedPlan ? 'default' : 'outline'}
                  onClick={() => setSelectedPlan(p)}
                >
                  {p.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Maybe Later
            </Button>
            <Button
              onClick={handleUpgrade}
              className="flex-1"
            >
              View Plans
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
